/**
 * Store du Codex.
 * Gere la progression de decouverte des entrees pour le joueur courant,
 * tout en laissant un acces complet aux administrateurs.
 */

import { CODEX_ENTRIES, CODEX_CATEGORIES, getCodexEntries, getCodexEntry } from '../domain/data/codex.js';
import { computed, reactive } from 'vue';
import { state } from './state.js';
import { isAdminUser } from './auth.js';
import { ACCOUNT_CHANGED_EVENT, readAccountItem, storedAccountId, writeAccountItem } from './accountStorage';
export { isAdminUser };

// Cle de persistance locale.  Ne pas modifier.
const STORAGE_KEY = 'guild_codex_progress';

/**
 * Etat reacif du codex.
 * discoveredIds : Set des IDs d'entrees deja decouvertes.
 */
const codexState = reactive({
  discoveredIds: new Set<string>(),
  unreadIds: new Set<string>(),
});
const UNREAD_KEY = 'guild_codex_unread';
let codexReviewActive = false;
export const unreadCodexCount = computed(() => codexState.unreadIds.size);

function saveUnreadCodex() {
  if (codexReviewActive) return;
  writeAccountItem(UNREAD_KEY, String(state.deviceId || storedAccountId() || ''), JSON.stringify([...codexState.unreadIds]));
}

/** Données isolées du banc : aucun enregistrement dans le dossier joueur. */
export function beginCodexNotificationReview() {
  if (!import.meta.env.DEV) return () => {};
  const previous = { discovered: codexState.discoveredIds, unread: codexState.unreadIds };
  codexReviewActive = true;
  const entries = CODEX_ENTRIES.filter(entry => entry.category === 'monsters').slice(0, 3);
  codexState.discoveredIds = new Set([...CODEX_ENTRIES.filter(entry => entry.unlockCondition === 'default').map(entry => entry.id), ...entries.map(entry => entry.id)]);
  codexState.unreadIds = new Set(entries.map(entry => entry.id));
  return () => {
    codexState.discoveredIds = previous.discovered;
    codexState.unreadIds = previous.unread;
    codexReviewActive = false;
  };
}

export function markCodexEntryRead(entryId: string) {
  if (codexState.unreadIds.delete(entryId)) saveUnreadCodex();
}

/**
 * Charge la progression depuis le stockage persistant du joueur.
 */
export function loadCodexProgress() {
  if (codexReviewActive) return;
  const saved = state.player?.codexProgress || [];
  if (Array.isArray(saved)) {
    codexState.discoveredIds = new Set(saved);
  } else {
    try {
      const parsed = JSON.parse(readAccountItem(STORAGE_KEY, storedAccountId()) || '[]');
      codexState.discoveredIds = new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
      codexState.discoveredIds = new Set();
    }
  }

  // Les entrees "default" sont toujours decouvertes.
  for (const entry of CODEX_ENTRIES) {
    if (entry.unlockCondition === 'default') {
      codexState.discoveredIds.add(entry.id);
    }
  }
  try {
    const unread = JSON.parse(readAccountItem(UNREAD_KEY, String(state.deviceId || storedAccountId() || '')) || '[]');
    codexState.unreadIds = new Set(Array.isArray(unread) ? unread.filter(id => codexState.discoveredIds.has(id) && CODEX_ENTRIES.some(entry => entry.id === id)) : []);
  } catch {
    codexState.unreadIds = new Set();
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener(ACCOUNT_CHANGED_EVENT, () => loadCodexProgress());
}

/**
 * Sauvegarde la progression dans le state joueur + localStorage.
 */
function saveCodexProgress() {
  if (codexReviewActive) return;
  const ids = Array.from(codexState.discoveredIds);
  if (!state.player) return;
  state.player.codexProgress = ids;
  try {
    writeAccountItem(STORAGE_KEY, String(state.deviceId || storedAccountId() || ''), JSON.stringify(ids));
  } catch {
    // ignore
  }
}

/**
 * Verifie si une entree est decouverte pour le joueur courant.
 * Les admins voient tout.
 */
export function isCodexDiscovered(entryId: string) {
  if (isAdminUser()) return true;
  return codexState.discoveredIds.has(entryId);
}

/**
 * Deverrouille une entree et sauvegarde.
 */
export function discoverCodexEntry(entryId: string, options: { notify?: boolean } = {}) {
  if (!entryId) return false;
  if (!CODEX_ENTRIES.some(entry => entry.id === entryId)) return false;
  if (codexState.discoveredIds.has(entryId)) return false;
  codexState.discoveredIds.add(entryId);
  saveCodexProgress();
  if (options.notify !== false) {
    codexState.unreadIds.add(entryId);
    saveUnreadCodex();
  }
  return true;
}

/**
 * Deverrouille toutes les entrees (commande admin).
 */
export function unlockAllCodex() {
  for (const entry of CODEX_ENTRIES) {
    codexState.discoveredIds.add(entry.id);
  }
  saveCodexProgress();
}

/**
 * Verifie si une categorie est deverrouillee (au moins une entree decouverte).
 * Les categories defaultUnlocked sont toujours accessibles.
 */
export function isCodexCategoryUnlocked(categoryId: string) {
  const cat = CODEX_CATEGORIES.find(c => c.id === categoryId);
  if (isAdminUser() || cat?.defaultUnlocked) return true;
  return CODEX_ENTRIES.some(e => e.category === categoryId && isCodexDiscovered(e.id));
}

/**
 * Declenche le deverrouillage automatique en fonction d'un evenement en jeu.
 * Types supportes :
 *   - 'encounter:<id>'  : rencontre un monstre
 *   - 'kill:<id>'        : vainc un monstre
 *   - 'obtain:<id>'      : obtient un item
 *   - 'level:<n>'        : atteint un niveau
 *   - 'class:<id>'       : devient une classe
 */
export function checkCodexUnlock(trigger: string) {
  if (!trigger) return;
  for (const entry of CODEX_ENTRIES) {
    if (isCodexDiscovered(entry.id)) continue;
    const cond = entry.unlockCondition || 'none';
    if (cond === 'default') continue;
    if (cond === trigger) {
      discoverCodexEntry(entry.id);
    }
  }
}

/**
 * Retourne les entrees filtrables du codex pour l'UI.
 * Chaque entree contient un flag `discovered`.
 */
export function getVisibleCodexEntries(category = null) {
  const isAdmin = isAdminUser();
  return CODEX_ENTRIES
    .filter(e => !category || e.category === category)
    .map(e => ({
      ...e,
      discovered: isAdmin || codexState.discoveredIds.has(e.id),
      unread: codexState.unreadIds.has(e.id),
    }));
}

/**
 * Compte des entrees decouvertes par categorie.
 */
export function getCodexProgressStats() {
  const stats: Record<string, { total: number; found: number }> = {};
  for (const cat of CODEX_CATEGORIES) {
    const all = CODEX_ENTRIES.filter(e => e.category === cat.id);
    const found = all.filter(e => codexState.discoveredIds.has(e.id)).length;
    stats[cat.id] = { total: all.length, found };
  }
  return stats;
}

export { CODEX_ENTRIES, CODEX_CATEGORIES, getCodexEntries, getCodexEntry };
